import React from "react";
import {
  KeyMetricCardWithChart,
  KEY_METRIC_CARD_SHELL_CLASS,
} from "../revenue_components/KeyMetricCardWithChart";
import { Skeleton } from "../shared/Skeleton";
import { useKeyMetricsExpense } from "../../hooks";

/** Skeleton matching compact KeyMetricCardWithChart shell */
function KeyMetricCardSkeleton({ withSubtext }: { withSubtext?: boolean }) {
  return (
    <div className={KEY_METRIC_CARD_SHELL_CLASS}>
      <Skeleton className="h-3 w-24 rounded" />
      <Skeleton className="h-10 w-36 rounded" />
      {withSubtext ? <Skeleton className="h-4 w-40 rounded" /> : null}
    </div>
  );
}

function ExpenseRevenueCardSkeleton() {
  return (
    <div className={KEY_METRIC_CARD_SHELL_CLASS}>
      <Skeleton className="h-3 w-36 rounded" />
      <Skeleton className="h-3 w-20 rounded" />
      <Skeleton className="h-12 w-28 rounded" />
      <Skeleton className="h-5 w-16 rounded-full" />
    </div>
  );
}

function ExpenseRevenueMetricCard({
  value,
  closedMonthLabel,
  badgeLabel,
}: {
  value: string;
  closedMonthLabel: string;
  badgeLabel?: string;
}) {
  return (
    <div className={KEY_METRIC_CARD_SHELL_CLASS}>
      <span className="text-caption font-caption text-neutral-400 uppercase tracking-wider">
        Expense / Revenue for {closedMonthLabel}
      </span>
      {/* <span className="text-body font-body text-neutral-600">
        {closedMonthLabel}
      </span> */}
      <span className="font-['Inter'] text-[48px] font-[700] leading-[48px] text-neutral-900 pt-2">
        {value}
      </span>
    </div>
  );
}

/** Key Metrics section: FY Expenses (closed months), DPO for latest closed month, Expense ÷ Revenue (latest closed month). Fetches via useKeyMetricsExpense (bootstrap keyMetrics). */
export function KeyMetricsExpenseSection() {
  const {
    ytdExpense,
    ytdSubtext,
    dpoAvgDays,
    dpoChangeText,
    dpoChangeVariant,
    dpoWindowLabel,
    gaugeValue,
    gaugeClosedMonthLabel,
    gaugeBadgeLabel,
    loading,
    error,
  } = useKeyMetricsExpense();

  if (loading) {
    return (
      <div className="flex w-full flex-col items-start gap-4">
        <div className="flex items-center gap-2">
          <div className="flex h-4 w-0.5 flex-none flex-col items-start bg-neutral-900" />
          <span className="font-['Inter'] text-[10px] font-[500] leading-[12px] text-neutral-600 uppercase tracking-widest">
            Key Metrics
          </span>
        </div>
        <div className="flex w-full gap-6 items-stretch [&>*]:min-h-[150px]">
          <KeyMetricCardSkeleton withSubtext />
          <KeyMetricCardSkeleton />
          <ExpenseRevenueCardSkeleton />
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col items-start gap-4">
      <div className="flex items-center gap-2">
        <div className="flex h-4 w-0.5 flex-none flex-col items-start bg-neutral-900" />
        <span className="font-['Inter'] text-[10px] font-[500] leading-[12px] text-neutral-600 uppercase tracking-widest">
          Key Metrics
        </span>
      </div>
      {error && <p className="text-body font-body text-error-600">{error}</p>}
      <div className="flex w-full gap-6 items-stretch [&>*]:min-h-[150px]">
        <KeyMetricCardWithChart
          title="FY Expenses (Closed)"
          value={ytdExpense}
          subtext={ytdSubtext}
          valueClassName="text-neutral-900"
        />
        <DpoKeyMetricCard
          avgDays={dpoAvgDays}
          changeText={dpoChangeText}
          changeVariant={dpoChangeVariant}
          windowLabel={dpoWindowLabel}
        />
        <ExpenseRevenueMetricCard
          value={gaugeValue}
          closedMonthLabel={gaugeClosedMonthLabel}
          badgeLabel={gaugeBadgeLabel}
        />
      </div>
    </div>
  );
}

function DpoKeyMetricCard({
  avgDays,
  changeText,
  changeVariant,
  windowLabel,
}: {
  avgDays: number | null;
  changeText?: string;
  changeVariant?: "up" | "down";
  windowLabel?: string;
}) {
  const displayAvg = avgDays != null ? String(avgDays) : "—";

  return (
    <div className={KEY_METRIC_CARD_SHELL_CLASS}>
      <span className="text-caption font-caption text-neutral-400 uppercase tracking-wider">
        DPO for {windowLabel ?? "Latest closed month"}
      </span>
      <span className="font-['Inter'] text-[48px] font-[700] leading-[48px] text-neutral-900">
        {displayAvg}
      </span>
      {changeText ? (
        <div className="flex items-center gap-2">
          <span
            className={`text-body font-body ${changeVariant === "up" ? "text-error-600" : "text-success-600"}`}
          >
            {changeText}
          </span>
          <span className="text-body font-body text-neutral-500">
            from last month
          </span>
        </div>
      ) : (
        <span className="text-body font-body text-neutral-500">
          from last month
        </span>
      )}
    </div>
  );
}
